import { IntegrationData } from "../types"

export const lumaData: IntegrationData = {
  platform: {
    slug: "luma",
    name: "Luma",
    description:
      "Turn every guest into an event promoter with Premagic's native Luma integration",
    shortDescription: "Native integration for community events",
    logoUrl: "https://cdn.brandfetch.io/lu.ma/w/512/h/512/logo",
    websiteUrl: "https://lu.ma/",
    category: "event-management",
    tags: ["Event Management", "Community Events"],
    isComingSoon: false,
  },
  heroTitle: "Supercharge Your Community Events with Shareable Posters",
  heroDescription:
    "We're excited to announce our native integration with Luma, the beloved event platform for communities, creators, and startups. This integration transforms your Luma guests into active event promoters – driving referrals and boosting registrations through personalized, shareable content.",
  challengeTitle: "The Challenge",
  challengeDescription:
    "You're using Luma to host your community events, meetups, and gatherings. It's simple, beautiful, and your audience loves it. Registrations are flowing in. But here's the thing: Luma makes registration easy, but it doesn't help your attendees spread the word. Your community members are excited – they just signed up for your event. That excitement is the perfect fuel for referrals. But without the right tools, that moment passes and the opportunity is lost.",
  challengePoints: [
    {
      title: "Missed Referral Moments",
      description:
        "Your guests are excited right after registration, but without shareable content, that momentum is lost. You need to capture that enthusiasm and turn it into promotion.",
    },
    {
      title: "Manual Content Creation",
      description:
        "Creating personalized event posters for each guest requires design skills and manual work. Without automation, you can't scale poster creation to your entire guest list.",
    },
    {
      title: "Limited Organic Growth",
      description:
        "Community events thrive on word-of-mouth, but your guests need the right tools to share. Without easy-to-use, shareable content, your reach stays limited.",
    },
  ],
  capabilities: [
    {
      title: "Auto-Generate Personalized Posters from Luma Guest Data",
      description:
        "Because we sync all guest information from your Luma events, we can automatically create pre-filled posters for every registrant. Each poster is personalized with guest's name, company, role, and event details.",
      features: [
        "Guest's name and company/organization",
        "Role or title from registration",
        "Event details and your branding",
        "No manual work or design skills needed",
      ],
    },
    {
      title: "Launch Campaigns to Drive Sharing and Referrals",
      description:
        "Don't wait for guests to share on their own. Proactively send poster campaigns to nudge them into action with email campaigns, social templates, and reminder sequences.",
      features: [
        "Email campaigns with personalized posters",
        "Social sharing templates ready to go",
        "Reminder sequences leading up to the event",
        "Track which guests are driving the most referrals",
      ],
    },
    {
      title: "Complete Guest Roster Sync",
      description:
        "The integration automatically imports your full guest list from Luma, including names, emails, phone numbers, approval status, and all registration question answers.",
      features: [
        "Full name and email address",
        "Phone number and approval status",
        "All custom registration question responses",
        "Ready for poster generation and campaigns",
      ],
    },
    {
      title: "Smart Custom Field Detection",
      description:
        "Luma lets you ask custom questions during registration. Premagic intelligently maps these to useful fields for poster personalization and targeting.",
      features: [
        "Company questions → Organization field",
        "LinkedIn questions → Social profile",
        "Role questions → Attendee segmentation",
        "All other questions → Custom fields for targeting",
      ],
    },
    {
      title: "Approval Status Awareness",
      description:
        "Luma supports invite-only and approval-based events. Premagic respects this, giving you control over who receives promotional content.",
      features: [
        "Approved guests ready for campaigns immediately",
        "Invited guests can be included or excluded",
        "Segment campaigns by approval status",
        "Maintain event access control",
      ],
    },
    {
      title: "AI-Powered Photo Distribution",
      description:
        "After the event, the integration powers personalized photo delivery. Event photos are tagged and distributed to attendees, keeping the community conversation going.",
      features: [
        "Event photos automatically tagged and distributed",
        "Every guest gets their own photo gallery",
        "Drive post-event engagement and social sharing",
        "Keep the community conversation going",
      ],
    },
  ],
  flywheelTitle: "The Community Referral Flywheel",
  flywheelDescription:
    "Here's how it works for community events: Each registration multiplies your reach.",
  flywheelSteps: [
    {
      number: 1,
      title: "Register",
      description: "Guest signs up on your Luma event page",
    },
    {
      number: 2,
      title: "Sync",
      description: "Premagic imports guest data automatically",
    },
    {
      number: 3,
      title: "Personalize",
      description: "Custom poster generated with their name and details",
    },
    {
      number: 4,
      title: "Campaign",
      description: "Send 'Share your attendance' email with poster",
    },
    {
      number: 5,
      title: "Share",
      description: "Guest posts to LinkedIn, Twitter, or shares via WhatsApp",
    },
    {
      number: 6,
      title: "Discover",
      description: "Their network sees a beautiful, branded event poster",
    },
    {
      number: 7,
      title: "Register",
      description: "New guests sign up through shared links",
    },
    {
      number: 8,
      title: "Repeat",
      description: "Every new registration multiplies your reach",
    },
  ],
  howItWorksSteps: [
    {
      number: 1,
      title: "Connect",
      description: "Link your Luma API key to Premagic",
    },
    {
      number: 2,
      title: "Select",
      description:
        "Choose which Luma event to connect to your Premagic project",
    },
    {
      number: 3,
      title: "Configure",
      description: "Select which guest fields to sync",
    },
    {
      number: 4,
      title: "Sync",
      description: "Import all guests with a single click",
    },
    {
      number: 5,
      title: "Generate",
      description: "Auto-create personalized posters for every guest",
    },
    {
      number: 6,
      title: "Campaign",
      description: "Send poster campaigns to drive sharing",
    },
    {
      number: 7,
      title: "Track",
      description: "Monitor referrals and registration growth",
    },
    {
      number: 8,
      title: "Deliver",
      description: "After the event, distribute photos to keep engagement high",
    },
  ],
  capabilitiesTable: [
    {
      feature: "Pre-filled Posters",
      description: "Auto-generate personalized posters from synced guest data",
    },
    {
      feature: "Campaign Distribution",
      description: "Send poster campaigns to drive sharing and referrals",
    },
    {
      feature: "Full Event Sync",
      description:
        "Import all Luma events with details, dates, and cover images",
    },
    {
      feature: "Guest Roster Import",
      description: "Sync names, emails, phone numbers, and approval status",
    },
    {
      feature: "Registration Answers",
      description: "Import all custom question responses from Luma",
    },
    {
      feature: "Smart Field Mapping",
      description:
        "Auto-detect company, LinkedIn, and role fields from questions",
    },
    {
      feature: "Approval Status Filtering",
      description: "Segment campaigns by approved vs. invited guests",
    },
    {
      feature: "Pagination Support",
      description: "Handle events and guest lists of any size",
    },
    {
      feature: "AI Photo Distribution",
      description: "Post-event photo delivery to keep engagement high",
    },
  ],
  targetAudience: [
    {
      title: "Community Builders",
      description: "Using Luma who want organic event growth through referrals",
    },
    {
      title: "Startup Founders",
      description: "Hosting launch events, demo days, or meetups",
    },
    {
      title: "Developer Advocates",
      description: "Running tech community events and meetups",
    },
    {
      title: "Creators and Educators",
      description: "Hosting workshops, masterclasses, and learning events",
    },
    {
      title: "Event Organizers",
      description: "Who love Luma's simplicity but want more promotional power",
    },
  ],
  ctaTitle:
    "Ready to Turn Your Luma Community Into a Registration Growth Engine?",
  ctaDescription:
    "Your Luma guests are your biggest fans. They signed up because they're excited about your event. They want to share – they just need the right tools. With Premagic's Luma integration, you can capture guest data, personalize at scale, activate your community, and drive registrations. Stop hoping your guests will share. Give them something worth sharing.",
  seo: {
    title: "Premagic + Luma Integration | Supercharge Community Events",
    description:
      "Transform your Luma community events with Premagic's native integration. Auto-generate personalized posters, sync guest data, and drive event registrations through word-of-mouth marketing.",
    keywords: [
      "luma",
      "event management",
      "community events",
      "event marketing",
      "referral marketing",
      "poster creation",
      "event promotion",
      "registration growth",
    ],
  },
}
